import { Cartesian3, Color, ConstantProperty, JulianDate, createGuid, } from "cesium";
import { AssetType, CESIUM_PICKABLE_ENTITY, SegmentType, } from "../../types";
import { PointType, } from "./types";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { getDefaultLineworkSettings, } from "../../settings/earthbrain-site-settings";
import { DEFAULT_TERRIAN_PROFILE_VIEW_STEP_SIZE } from "../road/defaults";
import { LineWorkUtils } from "../../utilities/line-work-utils";
import { calculateKeyPointIndices, insertSlopeBreaksIntoOutline, } from "../../utilities/line-profile-geometry-utils";
import { findNearestPointOnCenterline, interpolateAt, prepareCenterlineData, } from "../mid-path/mid-path-interpolation";
import { formatSPLabel, LABEL_BEGIN_ROAD_POINT, LABEL_END_ROAD_POINT, } from "./constants";
import { generateMidpointLabel } from "../../cross-sections/linework-cross-section-handler-helper";
export var ConstructionState;
(function (ConstructionState) {
    ConstructionState["UNDER_CONSTRUCTION"] = "under-construction";
    ConstructionState["UNDER_EDIT"] = "under-edit";
    ConstructionState["READY"] = "ready";
    ConstructionState["NONE"] = "none";
})(ConstructionState || (ConstructionState = {}));
export class ArbitraryLine {
    _controlPointMap;
    _labelsMap;
    _id;
    _associatedTerrain;
    _constructionState;
    // Cesium Entities
    _straightLineEntity;
    _viewer;
    _heightMap = undefined;
    _materialSetting;
    // Path info data for road-based arbitrary lines
    _pathInfo;
    // Slope breaks from road/trench (when centerline is created from road/trench)
    _slopeBreaks;
    // PathInfo-based centerline display
    _centerlineEntity;
    _centerlinePoints = [];
    _centerlineLabelsMap = new Map();
    _centerlineSpheresMap = new Map();
    _allPathPointSpheresMap = new Map();
    constructor(materialSetting, viewer, associatedTerrain) {
        this._id = createGuid();
        this._associatedTerrain = associatedTerrain;
        this._viewer = viewer;
        this._controlPointMap = new Map();
        this._labelsMap = new Map();
        this._materialSetting = materialSetting;
        this._straightLineEntity = EarthBrainUtils.createLineEntity(viewer, "lineworkStraightLine", [], this._materialSetting);
    }
    get id() {
        return this._id;
    }
    set id(newID) {
        this._id = newID;
    }
    set constructionState(state) {
        this._constructionState = state;
    }
    get constructionState() {
        return this._constructionState;
    }
    get heightMap() {
        return this._heightMap;
    }
    get pointsToCreateCrossSection() {
        return this.controlPoints.map((point, i) => ({
            id: point.sphere.id,
            pointType: point.pointType,
            point: point.pointInfo.localPosition,
            label: Array.from(this._labelsMap.values())[i]?.label.text.getValue(JulianDate.now()),
        }));
    }
    // public get pathBasedCrossSectionData():
    // 	| IPathBasedCrossSectionData
    // 	| undefined {
    // 	if (!this._pathInfo) {
    // 		return undefined;
    // 	}
    // 	return {
    // 		segments: this._pathInfo.segments,
    // 		pathPoints: this._pathInfo.pathPoints,
    // 		controlPoints: this.pointsToCreateCrossSection,
    // 	};
    // }
    get localControlPoints() {
        return this.controlPoints.map((point) => point.pointInfo.localPosition);
    }
    get spheres() {
        return this.controlPoints.map((point) => point.sphere);
    }
    get assetType() {
        return AssetType.ArbitraryLine_Data;
    }
    get controlPointLength() {
        return this._controlPointMap.size;
    }
    get lastControlPoint() {
        return Array.from(this._controlPointMap.values()).pop();
    }
    get firstControlPoint() {
        return Array.from(this._controlPointMap.values())[0];
    }
    get controlPoints() {
        return Array.from(this._controlPointMap.values());
    }
    get associatedTerrain() {
        return this._associatedTerrain;
    }
    zoomTo() {
        // Use centerlineEntity if PathInfo exists
        if (this._pathInfo && this._centerlineEntity) {
            this._viewer.zoomTo(this._centerlineEntity);
        }
        else {
            // Use straightLineEntity for Version 1.0 or when no PathInfo exists
            this._straightLineEntity && this._viewer.zoomTo(this._straightLineEntity);
        }
    }
    /**
     * Returns distance from last point of the road
     * @param newPoint
     * @returns number, distance is in km
     */
    getDistanceFromLastPoint(newPoint) {
        let distance = undefined;
        if (this.controlPoints.length != 0) {
            const lastPoint = this.controlPoints[this.controlPoints.length - 1].pointInfo.position;
            // distance returns in meters we need to convert it to the KM
            distance = Cartesian3.distance(newPoint, lastPoint);
            distance *= 0.001;
        }
        else {
            distance = 0;
        }
        return distance;
    }
    addControlPoint(pointInfo) {
        //if point is already exist then don't add
        const controlPointArray = Array.from(this._controlPointMap.values()).map((p) => p.pointInfo.position);
        if (controlPointArray.find((pos) => pos.equals(pointInfo.position))) {
            console.warn("The control point picked already exists.");
            return;
        }
        // create control point sphere
        const sphereEntity = this.getControlPointSphere(pointInfo.position);
        //detect point type
        const pointType = this._controlPointMap.size === 0
            ? PointType.BEGINNING_POINT
            : PointType.INTERMEDIATE_POINT;
        // store control point data
        this._controlPointMap.set(sphereEntity.id, {
            sphere: sphereEntity,
            pointInfo,
            pointType,
        });
        if (this._constructionState === ConstructionState.UNDER_CONSTRUCTION) {
            this._updatePathLine();
        }
        return sphereEntity.id;
    }
    getControlPointSphere(position) {
        const sphereEntity = EarthBrainUtils.addSphere(this._viewer, position, Color.TRANSPARENT);
        // Hide control point spheres by default for centerline display
        sphereEntity.show = false;
        sphereEntity.addProperty("pickInfo"); //Information when this sphere gets picked
        sphereEntity["pickInfo"] = {
            assetType: AssetType.ArbitraryLine_Data,
            pickableType: CESIUM_PICKABLE_ENTITY.ARBITRARY_LINE_CONTROL_POINT,
            entity: sphereEntity,
            owner: this,
        };
        return sphereEntity;
    }
    _updatePathLine() {
        this._straightLineEntity.polyline.positions = new ConstantProperty(Array.from(this._controlPointMap.values()).map((p) => p.sphere.position.getValue(JulianDate.now())));
    }
    createLabels() {
        //removing all labels
        this._removeAllLabels();
        let i = 1;
        this._controlPointMap.forEach((val, key) => {
            const pointTypeText = this._getTextBasedOnPointType(val.pointType);
            const text = pointTypeText === "IP" ? "IP" + i++ : pointTypeText;
            const label = EarthBrainUtils.addLabel(this._viewer, val.sphere.position.getValue(JulianDate.now()), text, {
                fillColor: Color.fromCssColorString("#FFFFFF"),
            });
            this._labelsMap.set(key, label);
        });
    }
    _getTextBasedOnPointType(pointType) {
        switch (pointType) {
            case PointType.BEGINNING_POINT:
                return "BP";
            case PointType.END_POINT:
                return "EP";
            case PointType.INTERMEDIATE_POINT:
                return "IP";
            case PointType.STATION_POINT:
                return "SP";
            case PointType.INTERPOLATED_POINT:
                return "NO.";
        }
    }
    _removeAllLabels() {
        this._labelsMap.forEach((label) => this._viewer.entities.remove(label));
        this._labelsMap.clear();
    }
    dispose() {
        this._controlPointMap.forEach((data) => {
            this._viewer.entities.remove(data.sphere);
        });
        this._removeAllLabels();
        this._viewer.entities.remove(this._straightLineEntity);
        // Remove centerline entity if it exists
        if (this._centerlineEntity) {
            this._viewer.entities.remove(this._centerlineEntity);
            this._centerlineEntity = undefined;
        }
        this._centerlinePoints = [];
        // Remove centerline labels and spheres
        this._removeCenterlineLabels();
        this._removeCenterlineSpheres();
        this._removeAllPathPointSpheres();
        this._controlPointMap.clear();
        this._id = "";
        this._straightLineEntity = undefined;
    }
    updateVisibility(show) {
        this._labelsMap.forEach((data) => {
            data.show = show;
        });
        // Control visibility based on pathInfo availability
        if (show) {
            // When showing, respect the pathInfo-based display logic
            this.updateCenterlineDisplay();
        }
        else {
            // When hiding, hide both lines and all labels/spheres
            this._straightLineEntity.show = false;
            if (this._centerlineEntity) {
                this._centerlineEntity.show = false;
            }
            // Hide centerline labels and spheres when visibility is off
            for (const label of this._centerlineLabelsMap.values()) {
                label.show = false;
            }
            for (const sphere of this._centerlineSpheresMap.values()) {
                sphere.show = false;
            }
            for (const sphere of this._allPathPointSpheresMap.values()) {
                sphere.show = false;
            }
        }
    }
    toJSON() {
        try {
            const controlPoints = Array.from(this._controlPointMap.values()).map((p) => ({
                point: [
                    p.pointInfo.localPosition.x,
                    p.pointInfo.localPosition.y,
                    p.pointInfo.localPosition.z,
                ],
                pointType: p.pointType,
            }));
            // Version 2.0: Include pathInfo if available
            if (this._pathInfo) {
                const arbitraryLineInfo = {
                    version: 2.0,
                    lineId: this._id,
                    controlPoints: controlPoints,
                    associatedTerrain: {
                        id: this._associatedTerrain.id,
                        terrainType: this._associatedTerrain.assetType,
                    },
                    pathInfo: this._pathInfo,
                };
                return JSON.stringify(arbitraryLineInfo);
            }
            // Version 1.0: Fallback to control points only (backward compatibility)
            const arbitraryLineInfo = {
                version: 1.0,
                lineId: this._id,
                controlPoints: controlPoints,
                associatedTerrain: {
                    id: this._associatedTerrain.id,
                    terrainType: this._associatedTerrain.assetType,
                },
            };
            return JSON.stringify(arbitraryLineInfo);
        }
        catch (error) {
            console.log(error);
        }
    }
    async addControlPoints(points, site) {
        //if point is already exist then don't add
        this._controlPointMap.forEach((data) => {
            this._viewer.entities.remove(data.sphere);
        });
        this._removeAllLabels();
        this._controlPointMap.clear();
        const wgs84Points = await site.convertPointsToWGS84(points.map((p) => p.point));
        // create control point sphere
        const ids = [];
        points.forEach((p, i) => {
            const sphereEntity = this.getControlPointSphere(wgs84Points[i]);
            // store control point data
            this._controlPointMap.set(sphereEntity.id, {
                sphere: sphereEntity,
                pointType: p.pointType,
                pointInfo: {
                    position: wgs84Points[i],
                    localPosition: p.point.clone(),
                },
            });
            ids.push(sphereEntity.id);
        });
        return ids;
    }
    static async fromJSON(json, viewer, site) {
        const parsedData = JSON.parse(json);
        let associatedTerrain;
        let pathInfo;
        // Handle different schema versions
        if (!parsedData.version) {
            // Legacy schema without version
            const arbitraryLineData = parsedData;
            const drawing2dImageId = arbitraryLineData.associateDrawing2dImageId;
            const pointCloudId = arbitraryLineData.associatePointCloudId;
            if (pointCloudId) {
                associatedTerrain = await site.getTerrain(pointCloudId, AssetType.Point_Cloud);
            }
            else if (drawing2dImageId) {
                associatedTerrain = await site.getTerrain(drawing2dImageId, AssetType.REFERENCE_TOPOLOGY);
            }
            else {
                throw new Error("No associated terrain found");
            }
        }
        else if (parsedData.version >= 1.0) {
            // Version 1.0 and above schema (2.0 adds pathInfo support)
            const arbitraryLineData = parsedData;
            associatedTerrain = await site.getTerrain(arbitraryLineData.associatedTerrain.id, arbitraryLineData.associatedTerrain.terrainType);
            // Version 2.0 and above includes pathInfo
            if (parsedData.version >= 2.0) {
                pathInfo = arbitraryLineData
                    .pathInfo;
            }
        }
        else {
            throw new Error(`Unsupported version: ${parsedData.version}`);
        }
        const arbitraryLine = new ArbitraryLine(getDefaultLineworkSettings(), viewer, associatedTerrain);
        // Store pathInfo if available
        if (pathInfo) {
            arbitraryLine._pathInfo = pathInfo;
        }
        // Store slopeBreaks if available (version 2.0 and above)
        if (parsedData.version >= 2.0 && parsedData.slopeBreaks) {
            arbitraryLine._slopeBreaks = parsedData.slopeBreaks;
        }
        const controlPoints = parsedData.controlPoints.map((p) => {
            const position = Cartesian3.fromArray(p.point);
            return { point: position, pointType: p.pointType };
        });
        await arbitraryLine.addControlPoints(controlPoints, site);
        arbitraryLine._updatePathLine();
        arbitraryLine.createLabels();
        // Create centerline from pathInfo if available
        if (pathInfo) {
            await arbitraryLine._createCenterlineFromPathInfo(site);
            await arbitraryLine._createCenterlineLabels(site);
        }
        // Update display based on pathInfo availability
        arbitraryLine.updateCenterlineDisplay();
        return arbitraryLine;
    }
    /**
     * Create centerline entity from pathInfo data
     * Generates a polyline along the actual centerline path
     */
    async _createCenterlineFromPathInfo(site, visible = true) {
        if (!this._pathInfo || !this._pathInfo.pathPoints) {
            return;
        }
        // Extract local positions from pathInfo
        const localPositions = this._pathInfo.pathPoints.map((pathPoint) => pathPoint.position);
        // Convert local coordinates to WGS84
        this._centerlinePoints = await site.convertPointsToWGS84(localPositions);
        // Create centerline entity if it doesn't exist
        if (!this._centerlineEntity) {
            this._centerlineEntity = EarthBrainUtils.createLineEntity(this._viewer, "pathInfoCenterline", this._centerlinePoints, this._materialSetting);
            this._centerlineEntity.show = visible;
        }
        else {
            // Update existing entity
            this._centerlineEntity.polyline.positions = new ConstantProperty(this._centerlinePoints);
        }
        // Add gold spheres to all pathInfo points
        this._createAllPathPointSpheres(visible);
    }
    /**
     * Update display based on pathInfo availability
     * Controls visibility of straight line vs curved centerline
     */
    updateCenterlineDisplay() {
        if (this._pathInfo) {
            // Version 2.0: Show curved centerline, hide straight line
            if (this._centerlineEntity) {
                this._centerlineEntity.show = true;
            }
            this._straightLineEntity.show = false;
            // Show centerline labels and spheres, hide control point labels
            for (const label of this._centerlineLabelsMap.values()) {
                label.show = true;
            }
            for (const sphere of this._centerlineSpheresMap.values()) {
                sphere.show = true;
            }
            for (const sphere of this._allPathPointSpheresMap.values()) {
                sphere.show = true;
            }
            for (const label of this._labelsMap.values()) {
                label.show = false;
            }
        }
        else {
            // Version 1.0: Show straight line, no centerline
            this._straightLineEntity.show = true;
            if (this._centerlineEntity) {
                this._centerlineEntity.show = false;
            }
            // Hide centerline labels and spheres, show control point labels
            for (const label of this._centerlineLabelsMap.values()) {
                label.show = false;
            }
            for (const sphere of this._centerlineSpheresMap.values()) {
                sphere.show = false;
            }
            for (const sphere of this._allPathPointSpheresMap.values()) {
                sphere.show = false;
            }
            for (const label of this._labelsMap.values()) {
                label.show = true;
            }
        }
    }
    /**
     * Create spheres for all pathInfo points
     * This includes both labeled points (BP/SP/BC/EC/EP) and non-labeled points
     */
    _createAllPathPointSpheres(visible = true) {
        // Remove existing spheres first
        this._removeAllPathPointSpheres();
        // Add a sphere at each centerline point
        for (const position of this._centerlinePoints) {
            const sphere = EarthBrainUtils.addSphere(this._viewer, position, Color.GOLD);
            sphere.show = visible;
            this._allPathPointSpheresMap.set(sphere.id, sphere);
        }
    }
    /**
     * Remove all path point spheres
     */
    _removeAllPathPointSpheres() {
        for (const sphere of this._allPathPointSpheresMap.values()) {
            this._viewer.entities.remove(sphere);
        }
        this._allPathPointSpheresMap.clear();
    }
    /**
     * Add a single centerline label
     */
    _addCenterlineLabel(position, text, visible = true) {
        const label = EarthBrainUtils.addLabel(this._viewer, position, text, {
            fillColor: Color.fromCssColorString("#FFFFFF"),
        });
        label.show = visible;
        this._centerlineLabelsMap.set(label.id, label);
    }
    /**
     * Add a sphere at centerline position
     */
    _addCenterlineSphere(position, visible = true) {
        const sphere = EarthBrainUtils.addSphere(this._viewer, position, Color.GOLD);
        sphere.show = visible;
        this._centerlineSpheresMap.set(sphere.id, sphere);
    }
    /**
     * Remove all centerline labels
     */
    _removeCenterlineLabels() {
        for (const label of this._centerlineLabelsMap.values()) {
            this._viewer.entities.remove(label);
        }
        this._centerlineLabelsMap.clear();
    }
    /**
     * Remove all centerline spheres
     */
    _removeCenterlineSpheres() {
        for (const sphere of this._centerlineSpheresMap.values()) {
            this._viewer.entities.remove(sphere);
        }
        this._centerlineSpheresMap.clear();
    }
    /**
     * Create centerline labels (BP/SP/BC/EC/EP)
     */
    async _createCenterlineLabels(site, visible = true) {
        // Remove existing centerline labels and spheres
        this._removeCenterlineLabels();
        this._removeCenterlineSpheres();
        if (!this._pathInfo || this._centerlinePoints.length === 0) {
            return;
        }
        // Create control point labels (BP/SP/EP)
        await this._createControlPointLabels(site, visible);
        // Create segment labels (BC/EC)
        await this._createSegmentLabels(site, visible);
    }
    /**
     * Create control point labels (BP/SP/EP)
     */
    async _createControlPointLabels(site, visible = true) {
        if (!this._pathInfo || this._centerlinePoints.length === 0) {
            return;
        }
        // // Import needed functions (these will be available from imports at top of file)
        // const { prepareCenterlineData, findNearestPointOnCenterline } =
        // 	await import("../mid-path/mid-path-interpolation");
        // const { LABEL_BEGIN_ROAD_POINT, LABEL_END_ROAD_POINT, formatSPLabel } =
        // 	await import("./constants");
        // Prepare centerline data for efficient nearest point queries
        const centerlineData = prepareCenterlineData(this._pathInfo.pathPoints);
        // BP: first point
        const bpPosition = this._centerlinePoints[0];
        this._addCenterlineLabel(bpPosition, LABEL_BEGIN_ROAD_POINT, visible);
        this._addCenterlineSphere(bpPosition, visible);
        // SP labels for intermediate control points
        const intermediatePoints = this.controlPoints.filter((cp) => cp.pointType === PointType.INTERMEDIATE_POINT);
        // For each intermediate control point, find nearest point on centerline
        let spIndex = 1;
        for (const controlPoint of intermediatePoints) {
            const localPosition = controlPoint.pointInfo.localPosition;
            // Find nearest point on centerline (using local coordinates)
            const nearestResult = findNearestPointOnCenterline(centerlineData, localPosition);
            // Convert the nearest point position from local to WGS84
            const wgs84Positions = await site.convertPointsToWGS84([
                nearestResult.position,
            ]);
            const spPosition = wgs84Positions[0];
            // Add SP label and sphere
            this._addCenterlineLabel(spPosition, formatSPLabel(spIndex), visible);
            this._addCenterlineSphere(spPosition, visible);
            spIndex++;
        }
        // EP: last point
        const epPosition = this._centerlinePoints[this._centerlinePoints.length - 1];
        this._addCenterlineLabel(epPosition, LABEL_END_ROAD_POINT, visible);
        this._addCenterlineSphere(epPosition, visible);
    }
    /**
     * Create segment labels (BC/EC)
     * Generates labels at the midpoint of CURVE segments
     */
    async _createSegmentLabels(site, visible = true) {
        if (!this._pathInfo || this._centerlinePoints.length === 0) {
            return;
        }
        // Prepare centerline data for efficient nearest point queries
        const centerlineData = prepareCenterlineData(this._pathInfo.pathPoints);
        // Calculate cumulative segment distances
        let cumulativeSegmentDistance = 0;
        const segmentStartDistances = [0];
        for (const segment of this._pathInfo.segments) {
            cumulativeSegmentDistance += segment.length;
            segmentStartDistances.push(cumulativeSegmentDistance);
        }
        // Process each CURVE segment
        for (let i = 0; i < this._pathInfo.segments.length; i++) {
            const segment = this._pathInfo.segments[i];
            // Only generate BC/EC for CURVE segments
            if (segment.type === SegmentType.CURVE) {
                // Calculate segment midpoint distance along the centerline
                const segmentStartDist = segmentStartDistances[i];
                const segmentMidDist = segmentStartDist + segment.length / 2;
                // Interpolate position at midpoint
                const midpointResult = interpolateAt(centerlineData, segmentMidDist);
                // Convert to WGS84
                const wgs84Positions = await site.convertPointsToWGS84([
                    midpointResult.position,
                ]);
                const midpointWGS84 = wgs84Positions[0];
                // Generate label (BC1, EC2/BC3, EC4, etc.)
                const label = generateMidpointLabel(segment.id, this._pathInfo.segments);
                // Add label and sphere
                this._addCenterlineLabel(midpointWGS84, label, visible);
                this._addCenterlineSphere(midpointWGS84, visible);
            }
        }
    }
    get2dViewerData(site) {
        const lineProfileData = {
            pointCloudOutline: [],
            lineWorksOutline: [],
            keyPointIndices: [],
            slopeBreakIndices: [],
        };
        // Use pathInfo.pathPoints if available (for road/trench-based centerlines),
        // otherwise fall back to localControlPoints (for manually created arbitrary lines)
        let pointsForDisplay;
        let keyPointIndices = [];
        if (this._pathInfo?.pathPoints) {
            // For pathInfo-based centerlines, use all pathPoints
            pointsForDisplay = this._pathInfo.pathPoints.map((p) => p.position);
            // Calculate key point indices for path-based centerlines
            const result = calculateKeyPointIndices(this._pathInfo.pathPoints, this._pathInfo.segments || []);
            keyPointIndices = result.keyPointIndices;
        }
        else {
            // For manually created arbitrary lines, use localControlPoints
            pointsForDisplay = this.localControlPoints;
            // All points are considered key points for manual lines
            for (let i = 0; i < pointsForDisplay.length; i++) {
                keyPointIndices.push(i);
            }
        }
        const arbitraryLineCartesians = LineWorkUtils.traceLineWorksOnPointCloud(pointsForDisplay, DEFAULT_TERRIAN_PROFILE_VIEW_STEP_SIZE);
        const pointCloudOutlines = [];
        const updatePointCloudRelatedData = () => {
            site.getPointClouds().forEach((data) => {
                if (data.tileSet.show) {
                    const outline = LineWorkUtils.createTilesetOutlineOfLineWorks(arbitraryLineCartesians, data);
                    pointCloudOutlines.push(outline);
                }
                lineProfileData.pointCloudOutline.push(...pointCloudOutlines);
            });
        };
        updatePointCloudRelatedData();
        // Create base outline from pointsForDisplay
        const outline = LineWorkUtils.createOutlineForLineWorks(pointsForDisplay);
        // Insert slope break points and recalculate indices
        let slopeBreakIndices = [];
        if (this._slopeBreaks && this._slopeBreaks.length > 0) {
            const result = insertSlopeBreaksIntoOutline(outline, keyPointIndices, this._slopeBreaks);
            keyPointIndices = result.keyPointIndices;
            slopeBreakIndices = result.slopeBreakIndices;
        }
        lineProfileData.lineWorksOutline = outline;
        lineProfileData.keyPointIndices = keyPointIndices;
        lineProfileData.slopeBreakIndices = slopeBreakIndices;
        // Debug log
        if (import.meta.env.DEV) {
            console.log("[ArbitraryLine] get2dViewerData:", {
                totalPoints: pointsForDisplay.length,
                keyPointIndices: keyPointIndices,
                slopeBreakIndices: slopeBreakIndices,
                controlPointsCount: this.controlPoints.length,
                hasPathInfo: !!this._pathInfo,
                hasSlopeBreaks: !!this._slopeBreaks,
            });
        }
        return lineProfileData;
    }
}
//# sourceMappingURL=earthbrain-arbitrary-line.js.map